import React, { useState, useEffect, useRef } from 'react';
import { IconStar, IconTrash, IconPlus } from './Icons';
import { saveField, loadUserData } from '../supabase';
import { ARENAS, ARENA_LABELS } from '../constants';

const TABS = ['Intentions', 'Vision Board'];

export default function Goals({ userId }) {
  const [tab, setTab] = useState(0);
  const [goals, setGoals] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({ goal: '', intention: '', visualization: '', why: '', barriers: '', timeline: '', steps: '', type: 'short', arena: '', mentors: [{ name: '', note: '' }, { name: '', note: '' }, { name: '', note: '' }] });
  const [showForm, setShowForm] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    async function load() {
      if (userId) {
        const data = await loadUserData(userId);
        setGoals(data?.goals || []);
        setPhotos(data?.photos || []);
      } else {
        try { setGoals(JSON.parse(localStorage.getItem('bhn_journal_goals') || '[]')); } catch {}
        try { setPhotos(JSON.parse(localStorage.getItem('bhn_vision_photos') || '[]')); } catch {}
      }
      setLoaded(true);
    }
    load();
  }, [userId]);

  async function persistGoals(updated) {
    setGoals(updated);
    if (userId) await saveField(userId, 'goals', updated);
    else localStorage.setItem('bhn_journal_goals', JSON.stringify(updated));
  }

  async function persistPhotos(updated) {
    setPhotos(updated);
    if (userId) await saveField(userId, 'photos', updated);
    else localStorage.setItem('bhn_vision_photos', JSON.stringify(updated));
  }

  function saveGoal() {
    if (!form.goal.trim()) return;
    // Parse steps with optional #branch syntax:
    //   Lines starting with '#' are branch headers (e.g. "#Finances").
    //   Steps below a header belong to that branch until the next header.
    //   Steps before any header (or in a list without '#') have branch = ''.
    const lines = form.steps.split('\n').map(l => l.trim()).filter(Boolean);
    let currentBranch = '';
    const steps = [];
    for (const line of lines) {
      if (line.startsWith('#')) {
        currentBranch = line.slice(1).trim();
      } else {
        steps.push({ text: line, done: false, branch: currentBranch });
      }
    }
    // Drop empty mentor slots so we don't save placeholders.
    const mentors = (form.mentors || []).filter(m => m.name && m.name.trim());
    persistGoals([...goals, { ...form, steps, mentors, id: Date.now(), created: new Date().toISOString() }]);
    setForm({ goal: '', intention: '', visualization: '', why: '', barriers: '', timeline: '', steps: '', type: 'short', arena: '', mentors: [{ name: '', note: '' }, { name: '', note: '' }, { name: '', note: '' }] });
    setShowForm(false);
  }

  function toggleStep(goalId, stepIdx) {
    persistGoals(goals.map(g => {
      if (g.id !== goalId) return g;
      const steps = g.steps.map((s, i) => i === stepIdx ? { ...s, done: !s.done } : s);
      return { ...g, steps };
    }));
  }

  function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setPendingPhoto({ src: ev.target.result, caption: '', goalId: '', branch: '', id: Date.now() + Math.random() });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function savePendingPhoto() {
    if (!pendingPhoto) return;
    try {
      persistPhotos([...photos, pendingPhoto]);
      setPendingPhoto(null);
    } catch { alert('Storage full — try removing some photos first.'); }
  }

  if (!loaded) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, width: '100%' }}>
      <div style={{ fontSize: 13, color: '#A09888' }}>Loading...</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', height: '100%' }}>
      <div className="top-bar">
        <div className="brand-small">Intentions</div>
        <div className="screen-title">Your Intentions</div>
      </div>
      <div style={{ display: 'flex', background: '#FFFFFF', borderBottom: '1px solid #F0EDE8', flexShrink: 0 }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{ flex: 1, padding: '12px 8px', background: 'none', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', letterSpacing: '.04em', textTransform: 'uppercase', color: tab === i ? '#111' : '#C8C4BE', borderBottom: tab === i ? '2px solid #111' : '2px solid transparent', transition: 'all .15s' }}>{t}</button>
        ))}
      </div>
      <div className="screen-body" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>

        {tab === 0 && (
          <div style={{ width: '100%' }}>
            {(showForm || goals.length === 0) ? (
              <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid #F5F3F0', width: '100%' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#111', marginBottom: 20 }}>New intention</div>
                <div style={{ marginBottom: 20 }}>
                  <div className="input-label">Intention type</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    {['short', 'long'].map(t => (
                      <button key={t} onClick={() => setForm(p => ({ ...p, type: t }))}
                        style={{ padding: '9px 20px', borderRadius: 100, border: '1px solid', borderColor: form.type === t ? '#3A6A2A' : '#E0DDD8', background: form.type === t ? '#3A6A2A' : 'white', color: form.type === t ? 'white' : '#777', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        {t === 'short' ? 'Short-term' : 'Long-term'}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div className="input-label">Which area of life does this serve?</div>
                  <select value={form.arena} onChange={e => setForm(p => ({ ...p, arena: e.target.value }))}
                    style={{ width: '100%', padding: '10px 0', border: 'none', borderBottom: '1px solid #E8E4DF', background: 'transparent', fontSize: 13, color: '#333', marginTop: 6, fontFamily: 'inherit', cursor: 'pointer' }}>
                    <option value="">Choose one (optional)...</option>
                    {ARENAS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                  </select>
                </div>
                {[
                  { key: 'goal', label: 'Name your intention *', placeholder: 'What are you moving toward?' },
                  { key: 'intention', label: 'Why are you starting this?', placeholder: 'Why now?' },
                  { key: 'visualization', label: 'How do I see this in my life?', placeholder: 'What does it look and feel like?' },
                  { key: 'why', label: 'Why is this important to me?', placeholder: 'Go deep' },
                  { key: 'barriers', label: 'What might get in the way?', placeholder: 'Where am I now vs. where I want to be?' },
                  { key: 'timeline', label: 'My timeline', placeholder: 'When do I want to be living this?' },
                ].map(({ key, label, placeholder }) => (
                  <div key={key} style={{ marginBottom: 18 }}>
                    <div className="input-label">{label}</div>
                    <textarea className="j-textarea" placeholder={placeholder} value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} rows={2} style={{ width: '100%' }} />
                  </div>
                ))}
                <div style={{ marginBottom: 18 }}>
                  <div className="input-label">Steps that serve this — one per line</div>
                  <textarea className="j-textarea" placeholder={"Step 1\nStep 2\nStep 3\n\nOptional — group with branches:\n#Finances\nSave $500\n#Logistics\nBook trip"} value={form.steps} onChange={e => setForm(p => ({ ...p, steps: e.target.value }))} rows={5} style={{ width: '100%' }} />
                  <div style={{ fontSize: 11, color: '#A09888', marginTop: 4, lineHeight: 1.6 }}>
                    Each line becomes a checkbox. Optional: start a line with <code style={{ background: '#F5F3F0', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>#</code> to group the steps below it under that name.
                  </div>
                </div>

                <div style={{ marginBottom: 18 }}>
                  <div className="input-label">Who's done what you want to do? (optional)</div>
                  <div style={{ fontSize: 11, color: '#A09888', marginBottom: 10, lineHeight: 1.6 }}>
                    Up to three people — author, friend, historical figure, anyone. Leave blank if you don't know yet.
                  </div>
                  {form.mentors.map((m, i) => (
                    <div key={i} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: i === 2 ? 'none' : '1px solid #F5F3F0' }}>
                      <input className="form-input" placeholder={`Name ${i + 1}`} value={m.name}
                        onChange={e => setForm(p => ({ ...p, mentors: p.mentors.map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))} />
                      <input className="form-input" placeholder="One action I can take — read their book, email them, etc."
                        value={m.note}
                        onChange={e => setForm(p => ({ ...p, mentors: p.mentors.map((x, j) => j === i ? { ...x, note: e.target.value } : x) }))} />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-primary" style={{ flex: 1, marginBottom: 0 }} onClick={saveGoal}>Save intention</button>
                  {goals.length > 0 && <button className="btn-secondary" style={{ flex: 1, marginBottom: 0 }} onClick={() => setShowForm(false)}>Cancel</button>}
                </div>
              </div>
            ) : (
              <button className="btn-primary" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={() => setShowForm(true)}>
                <IconPlus size={14} color="white" /> Add new intention
              </button>
            )}

            {goals.map(g => {
              const steps = Array.isArray(g.steps) ? g.steps : [];
              const doneCount = steps.filter(s => s.done).length;
              const pct = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;
              return (
                <div key={g.id} style={{ padding: '16px 0', borderBottom: '1px solid #F5F3F0', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1 }}>
                      <IconStar size={14} color="#A09888" />
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{g.goal}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={`goal-badge ${g.type === 'long' ? 'badge-l' : 'badge-s'}`}>{g.type === 'long' ? 'Long' : 'Short'}</div>
                      <button onClick={() => persistGoals(goals.filter(x => x.id !== g.id))} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: .4 }}><IconTrash size={13} /></button>
                    </div>
                  </div>
                  {g.arena && ARENA_LABELS[g.arena] && (
                    <div style={{ paddingLeft: 22, marginBottom: 8 }}>
                      <span style={{ display: 'inline-block', fontSize: 10, letterSpacing: '.08em', color: '#3A6A2A', background: '#F0F7EC', padding: '3px 10px', borderRadius: 100, fontWeight: 600, textTransform: 'uppercase' }}>
                        {ARENA_LABELS[g.arena]}
                      </span>
                    </div>
                  )}
                  {g.why && <div style={{ fontSize: 12, color: '#777', marginBottom: 8, lineHeight: 1.6, paddingLeft: 22 }}>{g.why}</div>}
                  {g.timeline && <div style={{ fontSize: 11, color: '#A09888', marginBottom: 10, paddingLeft: 22 }}>{g.timeline}</div>}
                  {steps.length > 0 && (
                    <div style={{ paddingLeft: 22 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: '#A09888', fontWeight: 600 }}>Progress</div>
                        <div style={{ fontSize: 11, color: '#3A6A2A', fontWeight: 700 }}>{pct}%</div>
                      </div>
                      <div className="goal-prog-bg" style={{ marginBottom: 10 }}><div className="goal-prog-fill" style={{ width: `${pct}%` }} /></div>
                      {(() => {
                        // Opt-in branches: if any step has a non-empty 'branch', render grouped.
                        // Otherwise render flat (original behavior).
                        const hasBranches = steps.some(s => s.branch && s.branch.trim());
                        if (!hasBranches) {
                          return steps.map((step, i) => {
                            // Find original index in g.steps for toggleStep.
                            const originalIdx = g.steps.indexOf(step);
                            return (
                              <div key={i} onClick={() => toggleStep(g.id, originalIdx)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #F8F6F4', cursor: 'pointer' }}>
                                <div style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${step.done ? '#3A6A2A' : '#D8D4CE'}`, background: step.done ? '#3A6A2A' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .15s' }}>
                                  {step.done && <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                </div>
                                <div style={{ fontSize: 12, color: step.done ? '#A09888' : '#333', textDecoration: step.done ? 'line-through' : 'none', lineHeight: 1.5 }}>{step.text}</div>
                              </div>
                            );
                          });
                        }
                        // Group by branch. Preserve first-seen order.
                        const groups = [];
                        const seen = new Map();
                        steps.forEach((step, flatIdx) => {
                          const b = (step.branch && step.branch.trim()) || '';
                          if (!seen.has(b)) {
                            seen.set(b, groups.length);
                            groups.push({ branch: b, items: [] });
                          }
                          groups[seen.get(b)].items.push({ step, flatIdx });
                        });
                        return groups.map((grp, gi) => {
                          const done = grp.items.filter(it => it.step.done).length;
                          return (
                            <div key={gi} style={{ marginBottom: 14 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>
                                  {grp.branch || 'Uncategorized'}
                                </div>
                                <div style={{ fontSize: 10, color: '#A09888' }}>{done} of {grp.items.length}</div>
                              </div>
                              <div style={{ paddingLeft: 8 }}>
                                {grp.items.map(({ step, flatIdx }) => (
                                  <div key={flatIdx} onClick={() => toggleStep(g.id, flatIdx)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #F8F6F4', cursor: 'pointer' }}>
                                    <div style={{ width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${step.done ? '#3A6A2A' : '#D8D4CE'}`, background: step.done ? '#3A6A2A' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .15s' }}>
                                      {step.done && <svg width="9" height="9" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                    </div>
                                    <div style={{ fontSize: 12, color: step.done ? '#A09888' : '#333', textDecoration: step.done ? 'line-through' : 'none', lineHeight: 1.5 }}>{step.text}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}

                  {Array.isArray(g.mentors) && g.mentors.length > 0 && (
                    <div style={{ paddingLeft: 22, marginTop: 14, paddingTop: 12, borderTop: '1px solid #F5F3F0' }}>
                      <div style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: '#A09888', fontWeight: 600, marginBottom: 8 }}>Who's done this</div>
                      {g.mentors.map((m, i) => (
                        <div key={i} style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>{m.name}</div>
                          {m.note && m.note.trim() && (
                            <div style={{ fontSize: 11, color: '#777', lineHeight: 1.5, marginTop: 2 }}>{m.note}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === 1 && (
          <div style={{ width: '100%' }}>
            <div style={{ fontSize: 13, color: '#A09888', lineHeight: 1.7, marginBottom: 20 }}>Images that hold what you're moving toward. Connect each photo to an intention.</div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />

            {/* Pending photo — save flow */}
            {pendingPhoto ? (
              <div style={{ marginBottom: 24, padding: 16, background: '#FAFAF8', borderRadius: 12, border: '1px solid #E8E4DF' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 12 }}>New photo</div>
                <img src={pendingPhoto.src} alt="Preview" style={{ width: '100%', borderRadius: 10, marginBottom: 12 }} />
                <div className="input-label" style={{ marginBottom: 6 }}>Connected intention</div>
                <select value={pendingPhoto.goalId || ''} onChange={e => setPendingPhoto(p => ({ ...p, goalId: e.target.value, branch: '' }))}
                  style={{ width: '100%', padding: '10px 0', border: 'none', borderBottom: '1px solid #E8E4DF', background: 'transparent', fontSize: 13, color: '#333', marginBottom: 12, fontFamily: 'inherit', cursor: 'pointer' }}>
                  <option value="">Select an intention...</option>
                  {goals.map(g => <option key={g.id} value={g.id}>{g.goal}</option>)}
                </select>
                {(() => {
                  // If the chosen intention has branches, offer to narrow down to one.
                  const chosen = goals.find(g => String(g.id) === String(pendingPhoto.goalId));
                  const branches = chosen && Array.isArray(chosen.steps)
                    ? [...new Set(chosen.steps.map(s => (s.branch && s.branch.trim()) || '').filter(Boolean))]
                    : [];
                  if (branches.length === 0) return null;
                  return (
                    <>
                      <div className="input-label" style={{ marginBottom: 6 }}>Connected branch (optional)</div>
                      <select value={pendingPhoto.branch || ''} onChange={e => setPendingPhoto(p => ({ ...p, branch: e.target.value }))}
                        style={{ width: '100%', padding: '10px 0', border: 'none', borderBottom: '1px solid #E8E4DF', background: 'transparent', fontSize: 13, color: '#333', marginBottom: 12, fontFamily: 'inherit', cursor: 'pointer' }}>
                        <option value="">No specific branch</option>
                        {branches.map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </>
                  );
                })()}
                <input className="form-input" style={{ marginBottom: 16 }} placeholder="Add a note to this photo..." value={pendingPhoto.caption || ''}
                  onChange={e => setPendingPhoto(p => ({ ...p, caption: e.target.value }))} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-primary" style={{ flex: 1, marginBottom: 0 }} onClick={savePendingPhoto}>Save to Vision Board</button>
                  <button className="btn-secondary" style={{ flex: 1, marginBottom: 0 }} onClick={() => setPendingPhoto(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              goals.length === 0 ? (
                <div style={{ background: '#FAFAF8', borderRadius: 12, padding: '16px', marginBottom: 20, border: '1px solid #E8E4DF', textAlign: 'center' }}>
                  <div style={{ fontSize: 13, color: '#A09888', lineHeight: 1.7 }}>Set an intention first in the Intentions tab — then connect your vision photos to it.</div>
                </div>
              ) : (
                <button className="btn-secondary" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={() => fileRef.current.click()}>
                  <IconPlus size={14} color="#333" /> Add photo from camera roll
                </button>
              )
            )}

            {photos.length === 0 && !pendingPhoto ? (
              <div style={{ textAlign: 'center', padding: '24px 0', borderTop: '1px solid #F5F3F0' }}>
                <div style={{ fontSize: 12, color: '#C8C4BE', lineHeight: 1.7 }}>Your vision photos will appear here.</div>
              </div>
            ) : (
              photos.map(photo => (
                <div key={photo.id} style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid #F5F3F0' }}>
                  <div style={{ width: '100%', borderRadius: 12, overflow: 'hidden', position: 'relative', background: '#F5F3F0', marginBottom: 12 }}>
                    <img src={photo.src} alt="Vision" style={{ width: '100%', display: 'block' }} />
                    <button onClick={() => persistPhotos(photos.filter(p => p.id !== photo.id))}
                      style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
                    </button>
                  </div>
                  {photo.goalId && goals.find(g => String(g.id) === String(photo.goalId)) && (
                    <div style={{ fontSize: 11, color: '#3A6A2A', fontWeight: 600, marginBottom: 6 }}>
                      ↳ {goals.find(g => String(g.id) === String(photo.goalId))?.goal}
                      {photo.branch && photo.branch.trim() && (
                        <span style={{ marginLeft: 8, fontSize: 10, letterSpacing: '.06em', color: '#3A6A2A', background: '#F0F7EC', padding: '2px 8px', borderRadius: 100, fontWeight: 600, textTransform: 'uppercase' }}>
                          {photo.branch}
                        </span>
                      )}
                    </div>
                  )}
                  {photo.caption && <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>{photo.caption}</div>}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
